import type {ReactElement} from 'react';
import {useEffect, useRef} from 'react';
import {SocialPlatform} from '@Components/social-media/social-media.types';
import {
  addConnectAccountBroadcastChannel,
  getAccountConnectionResponseFromBroadcast,
  handleAccountConnectionResponse,
  startAccountConnection,
  removeConnectAccountBroadcastChannel,
} from '@Libraries/social-media-account.library';
import {getDisplayableSocialPlatformURL, getRedirectableSocialPlatformURL, getSocialPlatformName} from '@Libraries/social-media-platform.library';
import {Icon} from '@Components/icon-v2';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {SocialThumbnail} from '@Components/social-media/components/social-thumbnail';
import {Text, TextSize} from '@Components/text';
import {Button, Size, Type} from '@Components/button';
import {updateTriggerNext, updateTriggerPrevious} from '@Components/social-media-wizard/social-media-wizard-reducer';
import {GA4EventName, GA4EventParamName, trackPublishingGA4Events} from '@Libraries/ga-events';
import {useAppDispatch} from '@/hooks';
import {isUserLoggedIn} from '@Libraries/user.library';
import {openSocialMediaWizardAuthenticateModal} from '@Libraries/social-media-wizard-library';
import {noop} from '@Utils/general.util';
import {useReloadAccountsAndOfferSelection} from '@Components/social-media-wizard/components/social-accounts-list/social-accounts-list.hooks';
import styles from './social-account-connection.module.scss';

interface SocialAccountConnectionProps {
  accountTypeToConnect: SocialPlatform;
}

function SocialAccountConnectionHeader({onBack, socialPlatform, accountType}: {onBack: () => void; accountType: SocialPlatform; socialPlatform: string}): ReactElement<any> {
  return (
    <div className="spacing-m-b-4 flex-row-align-center">
      <Icon icon="icon-arrow-left" size={IconSize.SIZE_ICON_20} type={IconType.GHOST} shape={IconShape.SQUARE} className="spacing-m-r-2" onClick={onBack} />
      <SocialThumbnail type={accountType} />
      <Text size={TextSize.SMALL} className="spacing-m-l-2" val={window.i18next.t('pmwjs_publish_to_x', {socialPlatform})} bold />
    </div>
  );
}

export function SocialAccountConnectionInstructions({
  accountType,
  onCancel,
  isFullWidth,
  onAccountConnected = noop,
}: {
  accountType: SocialPlatform;
  onCancel: () => void;
  isFullWidth: boolean;
  onAccountConnected?: () => void;
}): ReactElement<any> {
  const onAccountCreated = (): void => {
    onAccountConnected();
  };

  const onConnect = async (): Promise<void> => {
    if (isUserLoggedIn()) {
      startAccountConnection(accountType, onAccountCreated);
    } else {
      openSocialMediaWizardAuthenticateModal((): void => {
        startAccountConnection(accountType, onAccountCreated);
      });
    }
  };

  const getConnectionSetupText = (): string => {
    switch (accountType) {
      case SocialPlatform.INSTAGRAM:
        return window.i18next.t('pmwjs_instagram_connection_guide');
      default:
        return window.i18next.t('pmwjs_publish_go_to_x', {
          url: getRedirectableSocialPlatformURL(accountType),
          urlText: getDisplayableSocialPlatformURL(accountType),
        });
    }
  };

  const getConnectionCompleteText = (): string => {
    switch (accountType) {
      case SocialPlatform.INSTAGRAM:
        return window.i18next.t('pmwjs_instagram_connection_connection_message', {
          helpURL: 'https://support.postermywall.com/hc/en-us/articles/4410596800909-How-do-I-post-on-Instagram',
        });
      default:
        return window.i18next.t('pmwjs_connect_account');
    }
  };

  return (
    <>
      <div className={isFullWidth ? 'spacing-m-b-10' : 'spacing-m-b-4'}>
        <ol className={styles.connectionStepsList}>
          <li>
            <Text val={getConnectionSetupText()} dangerouslySetInnerHTML size={TextSize.XSMALL} />
          </li>
          <li>
            <Text val={getConnectionCompleteText()} dangerouslySetInnerHTML size={TextSize.XSMALL} />
          </li>
        </ol>
      </div>
      <div className="flex-h-row flex-content-end">
        <Button
          customClasses={`spacing-m-r-2 ${isFullWidth ? '_full-width spacing-m-r-2' : ''} `}
          type={Type.SECONDARY}
          size={Size.SMALL}
          text={window.i18next.t('pmwjs_cancel')}
          onClick={onCancel}
        />
        <Button customClasses={isFullWidth ? '_full-width' : ''} type={Type.PRIMARY} size={Size.SMALL} text={window.i18next.t('pmwjs_connect')} onClick={onConnect} />
      </div>
    </>
  );
}

export function SocialAccountConnection({accountTypeToConnect}: SocialAccountConnectionProps): ReactElement<any> | null {
  const platformName = getSocialPlatformName(accountTypeToConnect);
  const dispatch = useAppDispatch();
  const reloadAccountsAndOfferSelection = useReloadAccountsAndOfferSelection();
  const hasAdvancedWizardForThisConnectionRef = useRef(false);

  const isAccountCreated = (broadcastEvent: MessageEvent): void => {
    const connectionResponse = getAccountConnectionResponseFromBroadcast(broadcastEvent);
    handleAccountConnectionResponse(connectionResponse, () => {
      if (hasAdvancedWizardForThisConnectionRef.current) {
        return;
      }

      hasAdvancedWizardForThisConnectionRef.current = true;
      triggerWizardUpdatedOnAccountCreation(connectionResponse?.socialMediaPlatform);
    });
  };

  const triggerWizardUpdatedOnAccountCreation = (connectedPlatform: SocialPlatform | undefined): void => {
    void reloadAccountsAndOfferSelection(connectedPlatform ?? accountTypeToConnect);
    dispatch(updateTriggerNext());
    trackPublishingGA4Events(GA4EventName.ACCOUNT_ADDED, {[GA4EventParamName.PLATFORM]: platformName});
    trackPublishingGA4Events(GA4EventName.SOCIALWIZARD_ACCOUNTADDED, {[GA4EventParamName.PLATFORM]: platformName});
  };

  const onCancel = (): void => {
    dispatch(updateTriggerPrevious());
  };

  useEffect(() => {
    const accountCreatedBroadcastChannel = addConnectAccountBroadcastChannel(isAccountCreated);

    return (): void => {
      removeConnectAccountBroadcastChannel(accountCreatedBroadcastChannel);
    };
  }, []);

  return (
    <div className="flex-v-row">
      <SocialAccountConnectionHeader onBack={onCancel} accountType={accountTypeToConnect} socialPlatform={platformName} />
      <SocialAccountConnectionInstructions accountType={accountTypeToConnect} onCancel={onCancel} isFullWidth={false} />
    </div>
  );
}
